import { existsSync, promises as fs } from 'node:fs';
import path from 'node:path';
import { consola } from 'consola';
import {
  containerConfigPath,
  containerDir,
  containerEnvPath,
  monocerosHome as defaultMonocerosHome,
  prettyPath,
} from '../config/paths.js';
import { REGEX } from '../config/schema.js';
import {
  cleanupDockerObjects,
  composeProjectName,
  spawnDocker,
  type DockerExec,
} from '../devcontainer/compose.js';
import { ideStateVolumes } from '../create/scaffold.js';
import {
  backupServiceDataVolumes,
  composeServiceDataVolumes,
} from './service-data-volumes.js';
import { stopBridgeDaemon } from '../devcontainer/bridge-daemon.js';
import { removeSshAttach } from '../devcontainer/ssh-attach.js';
import { maybeStopProxy } from '../proxy/index.js';
import { removeDynamicConfig } from '../proxy/dynamic.js';

/**
 * `monoceros remove <name>` — wipe everything belonging to one
 * container.
 *
 * What "everything" means in practice (in this order):
 *
 *   1. Stop and remove docker objects scoped to the container:
 *        - compose containers (label `com.docker.compose.project=<project>`)
 *        - any image-mode container matching `vsc-<name>-*`
 *        - the project network `<project>_default`
 *      Named volumes: the IDE-state ones go here, the per-service data
 *      volumes (`monoceros-<name>-data-<svc>`, ADR 0036) only after the
 *      backup below has copied them out.
 *
 *   2. Optionally back up the host-side state:
 *        - `container-configs/<name>.yml`
 *        - `container/<name>/` (entire scaffold incl. `home/`,
 *          `projects/`, `.monoceros/`)
 *        - each service data volume, copied into `container/data/<svc>/`
 *      Lands at `container-backups/<name>-<timestamp>/`. Plain
 *      directory copy — readable with normal filesystem tools.
 *
 *   3. Delete the host-side state.
 *
 * Shared docker images (`monoceros-runtime:dev`, feature build
 * images, postgres/mysql/redis base images) are NOT removed — they
 * are shared across containers and pruning them is a separate
 * operation the builder can do with `docker image prune` when they
 * actually want to free that disk.
 */

export interface RunRemoveOptions {
  name: string;
  /** When true, skip the backup step. */
  noBackup?: boolean;
  /** Override of the user-data home. Tests inject a tmpdir. */
  monocerosHome?: string;
  /** Override the timestamp embedded in the backup directory name. */
  now?: Date;
  /**
   * Docker exec for the cleanup pipeline (ps/rm/network/run). Tests
   * inject a stub. Replaces the previous `dockerSpawn: ComposeSpawn`
   * which drove a bash script — direct docker spawn dodges the
   * Windows quoting issues on backslash-bearing label values.
   */
  dockerExec?: DockerExec;
  /** Override the docker exec used by the Traefik proxy lifecycle. */
  proxyDocker?: DockerExec;
  logger?: {
    info: (msg: string) => void;
    success: (msg: string) => void;
    warn?: (msg: string) => void;
  };
}

export interface RunRemoveResult {
  /** Path the yml was at before deletion, or `null` if it didn't exist. */
  configPath: string | null;
  /** Path the container scaffold was at before deletion, or `null`. */
  containerPath: string | null;
  /** Directory of the backup, or `null` when --no-backup was passed. */
  backupPath: string | null;
  /** Exit code of the docker cleanup step (0 on success). */
  dockerExitCode: number;
}

export async function runRemove(
  opts: RunRemoveOptions,
): Promise<RunRemoveResult> {
  const home = opts.monocerosHome ?? defaultMonocerosHome();
  const logger = opts.logger ?? {
    info: (msg) => consola.info(msg),
    success: (msg) => consola.success(msg),
    warn: (msg) => consola.warn(msg),
  };

  if (!REGEX.solutionName.test(opts.name)) {
    throw new Error(
      `Invalid config name: ${JSON.stringify(opts.name)}. Use letters, digits, '.', '_' or '-'.`,
    );
  }

  const ymlPath = containerConfigPath(opts.name, home);
  const envPath = containerEnvPath(opts.name, home);
  const containerPath = containerDir(opts.name, home);
  const hasYml = existsSync(ymlPath);
  const hasEnv = existsSync(envPath);
  const hasContainer = existsSync(containerPath);

  if (!hasYml && !hasContainer) {
    throw new Error(
      `Nothing to remove for '${opts.name}': neither ${ymlPath} nor ${containerPath} exists.`,
    );
  }

  // ── Step 1: stop + remove docker objects ────────────────────────
  // Four overlapping filters because devcontainer-cli ranges over
  // multiple naming/labeling schemes depending on container mode:
  //   1. compose-mode containers carry the compose project label
  //   2. image-mode + feature-build intermediates carry the
  //      devcontainer.local_folder label — the most reliable anchor,
  //      because @devcontainers/cli lets Docker assign random names
  //      like 'kind_cerf' that neither name-prefix filter catches.
  //   3. container-name prefix as a fallback for half-broken state
  //   4. deterministic `vsc-<name>-` prefix from older
  //      devcontainer-cli versions
  // All four are union'd, deduplicated, and `docker rm -f`-ed
  // together via cleanupDockerObjects() (direct Node spawn of docker,
  // no shell wrapper).
  const projectName = composeProjectName(containerPath);
  const dockerExec = opts.dockerExec ?? spawnDocker;
  const { exitCode: dockerExitCode } = await cleanupDockerObjects({
    projectName,
    filters: [
      `label=com.docker.compose.project=${projectName}`,
      `label=devcontainer.local_folder=${containerPath}`,
      `name=^${projectName}-`,
      `name=^vsc-${opts.name}-`,
    ],
    network: `${projectName}_default`,
    logTag: 'remove',
    logger,
    exec: dockerExec,
  });

  // Remove the per-container IDE-state named volumes (extensions, user
  // settings, JetBrains project caches). Unlike the bind-mounted home/
  // dirs, these live outside the container directory, so they must be
  // deleted explicitly or they'd leak after `remove`. `-f` ignores
  // volumes that were never created; the containers using them are
  // already gone from step 1, so they are free to delete. Best-effort.
  //
  // SHARED volumes (e.g. the machine-wide JetBrains backend at
  // `monoceros-jetbrains-remotedev`) are deliberately NOT deleted here -
  // other containers depend on them, like the Traefik proxy singleton.
  // Reclaim them explicitly with `docker volume rm` when no container
  // needs them. See ADR 0022.
  const ideVolumes = ideStateVolumes(opts.name)
    .filter((v) => !v.shared)
    .map((v) => v.volume);
  await dockerExec(['volume', 'rm', '-f', ...ideVolumes]);

  // The service data volumes (ADR 0036) hold the databases, so unlike the
  // IDE volumes they are NOT deleted here: they get copied into the backup
  // in step 2 first and are deleted after it.
  const dataVolumes = await composeServiceDataVolumes(containerPath, opts.name);

  // ── Step 2: optional backup ────────────────────────────────────
  let backupPath: string | null = null;
  if (!opts.noBackup && (hasYml || hasContainer)) {
    const ts = (opts.now ?? new Date()).toISOString().replace(/[:.]/g, '-');
    backupPath = path.join(home, 'container-backups', `${opts.name}-${ts}`);
    await fs.mkdir(backupPath, { recursive: true });
    if (hasYml) {
      await fs.copyFile(ymlPath, path.join(backupPath, `${opts.name}.yml`));
    }
    // The per-container env file holds the values behind the yml's
    // `${VAR}` references (secrets). It must travel with the backup, or
    // a restore would bring back a yml that can't be applied.
    if (hasEnv) {
      await fs.copyFile(envPath, path.join(backupPath, `${opts.name}.env`));
    }
    if (hasContainer) {
      const dst = path.join(backupPath, 'container');
      try {
        await fs.cp(containerPath, dst, { recursive: true });
      } catch (err) {
        const code = (err as NodeJS.ErrnoException).code;
        if (code !== 'EACCES' && code !== 'EPERM') {
          throw err;
        }
        // Same root-owned-files quirk the delete step handles below: the
        // container creates files the unprivileged monoceros process can't
        // read (the 0600 SSH host key, root-owned service data dirs). The
        // plain fs.cp can't copy them — fall back to a throw-away alpine
        // container (root) to copy the tree, preserving perms/owners.
        logger.info(
          `[remove] backup hit ${code} on root-owned files; copying via a throw-away alpine container…`,
        );
        await fs.mkdir(dst, { recursive: true });
        const { exitCode, stderr } = await dockerExec([
          'run',
          '--rm',
          '-v',
          `${containerPath}:/src:ro`,
          '-v',
          `${dst}:/dst`,
          'alpine:3.21',
          'sh',
          '-c',
          'cp -a /src/. /dst/',
        ]);
        if (exitCode !== 0) {
          throw new Error(
            `docker-based backup of ${containerPath} failed (exit ${exitCode}` +
              `${stderr.trim() ? `: ${stderr.trim()}` : ''}).`,
          );
        }
      }
      // Databases live in docker volumes, not in the copied tree, so they
      // need their own copy step into the same `data/<svc>/` layout the
      // bind mount used to produce. Restore + apply put them back from
      // there.
      if (dataVolumes.length > 0) {
        await backupServiceDataVolumes({
          volumes: dataVolumes,
          backupContainerDir: dst,
          dockerExec,
          logger,
        });
      }
    }
    logger.info(`Backup written to ${prettyPath(backupPath)}.`);
  }

  // Now that the data is in the backup (or the builder asked for none),
  // the data volumes can go. `-f` no-ops on volumes that never existed.
  if (dataVolumes.length > 0) {
    await dockerExec([
      'volume',
      'rm',
      '-f',
      ...dataVolumes.map((v) => v.volume),
    ]);
  }

  // Stop the host-side browser-bridge daemon (if running) before we delete
  // the container dir its pid file lives in. It would self-exit once it
  // noticed the container gone, but stopping it here is immediate and avoids
  // racing the directory removal. Best-effort.
  if (hasContainer) {
    await stopBridgeDaemon(containerPath);
  }

  // ── Step 3: delete host-side state ─────────────────────────────
  if (hasYml) {
    await fs.rm(ymlPath, { force: true });
  }
  if (hasEnv) {
    await fs.rm(envPath, { force: true });
  }
  if (hasContainer) {
    try {
      await fs.rm(containerPath, { recursive: true, force: true });
    } catch (err) {
      const code = (err as NodeJS.ErrnoException).code;
      if (code !== 'EACCES' && code !== 'EPERM') {
        throw err;
      }
      // Linux + rootful Docker quirk: bind-mounted service data
      // dirs (postgres, mysql, …) and container-generated secrets
      // (the 0600 SSH host keys, the root-owned keycloak import dir)
      // end up owned by the container process's UID — root for the
      // official images. The unprivileged monoceros process can't
      // unlink them.
      //
      // Fall back to docker as the cleanup actor: alpine runs as root
      // and deletes the tree. We mount the PARENT directory and
      // `rm -rf` the named child (rather than emptying the dir in
      // place), because:
      //   - `rm -rf` clears everything in one authoritative pass —
      //     hidden entries (`.monoceros/`) and root-owned files alike;
      //   - the previous `busybox find -mindepth 1 -delete` aborted on
      //     the first root-owned entry it hit (exit 1, nothing removed,
      //     empty stderr) on WSL2 / Docker Desktop bind mounts, leaving
      //     the whole tree behind. `rm -rf` as root does not.
      // The name is validated against REGEX.solutionName above, so it
      // cannot smuggle a path traversal into the mounted parent.
      //
      // macOS / Docker Desktop / rootless Docker on the host's own FS
      // rarely reach this branch — the happy fs.rm above succeeds
      // because files are user-owned through the VM / userns layer.
      const parentDir = path.dirname(containerPath);
      const childName = path.basename(containerPath);
      logger.info(
        `[remove] host-side rm hit ${code} on ${prettyPath(containerPath)}; using a throw-away alpine container to clean root-owned files…`,
      );
      const { exitCode: exit, stderr } = await dockerExec([
        'run',
        '--rm',
        '-v',
        `${parentDir}:/parent`,
        'alpine:3.21',
        'rm',
        '-rf',
        `/parent/${childName}`,
      ]);
      // `rm -rf` never fails on files it successfully removed, so its
      // exit code is meaningful — but the real verdict is whether the
      // host can confirm the directory is gone. After a clean alpine rm
      // the path no longer exists and this no-ops (force ignores
      // ENOENT); if something genuinely survived, this throws and we
      // surface it (with alpine's stderr) for manual cleanup.
      try {
        await fs.rm(containerPath, { recursive: true, force: true });
      } catch (err2) {
        const code2 = (err2 as NodeJS.ErrnoException).code;
        throw new Error(
          `docker-based cleanup of ${containerPath} did not fully clear it ` +
            `(alpine rm exit ${exit}${stderr.trim() ? `: ${stderr.trim()}` : ''}; ` +
            `host rm: ${code2}). Inspect with \`sudo ls -la ${containerPath}\` and clean manually.`,
        );
      }
    }
  }

  logger.success(
    `Removed '${opts.name}': docker objects gone, container-configs entry deleted, container directory deleted.`,
  );
  if (!backupPath) {
    logger.warn?.(
      'No backup created (--no-backup). The host-side state is gone for good.',
    );
  }

  // Drop the host-side SSH attach artifacts (proxy script + config.d
  // entry under <home>/ssh/). The keypair lived under the container dir
  // and is already gone with the directory delete above. The `Include`
  // line in ~/.ssh/config stays - it harmlessly globs and other
  // containers still rely on it. See ADR 0022.
  try {
    await removeSshAttach(home, opts.name);
  } catch (err) {
    logger.warn?.(
      `Could not remove SSH attach config for ${opts.name}: ${err instanceof Error ? err.message : String(err)}. Ignored.`,
    );
  }

  // Drop the container's Traefik dynamic-config file so a future
  // container with the same yml-name (re-init after remove) starts
  // with a clean slate. No-op when the file is absent.
  try {
    await removeDynamicConfig(opts.name, { monocerosHome: home });
  } catch (err) {
    logger.warn?.(
      `Could not remove Traefik dynamic config for ${opts.name}: ${err instanceof Error ? err.message : String(err)}. Ignored.`,
    );
  }

  // Tear down the Traefik singleton if this was the last container
  // attached to its network. See ADR 0007 (variant A — stop and
  // remove are treated identically).
  try {
    await maybeStopProxy({
      ...(opts.proxyDocker ? { docker: opts.proxyDocker } : {}),
      monocerosHome: home,
      logger: { info: (msg) => logger.info(msg), warn: logger.warn },
    });
  } catch (err) {
    logger.warn?.(
      `Could not tear down the Traefik proxy: ${err instanceof Error ? err.message : String(err)}. Ignored.`,
    );
  }

  return {
    configPath: hasYml ? ymlPath : null,
    containerPath: hasContainer ? containerPath : null,
    backupPath,
    dockerExitCode,
  };
}
